import { Box, FormField, TextInput, Button } from "grommet";
import React from "react";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { AliasBadgeViewModel, BadgeViewModel, GetBadge } from "./BadgeStore";
import QRCode from "qrcode";

const BadgePrint = ({ badges }: { badges: AliasBadgeViewModel[] }) => {
    const pixelFactor = 5;

    const [columns, setColumns] = React.useState(6);
    const [rows, setRows] = React.useState(8);
    const [startFrom, setStartFrom] = React.useState(1);
    const [size, setSize] = React.useState(22 * pixelFactor);
    const [verticalBorder, setVerticalBorder] = React.useState(9 * (pixelFactor - 1));
    const [horizontalBorder, setHorizontalBorder] = React.useState(5 * pixelFactor);
    const [horizontalSpacing, setHorizontalSpacing] = React.useState(11 * pixelFactor);
    const [verticalSpacing, setVerticalSpacing] = React.useState(13 * pixelFactor);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [canvasImage, setCanvasImage] = useState<string | null>(null);

    const buildURL = (id: string) => {
        return `${window.location.protocol}//${window.location.host}/ue/badges/${id}`;
    };

    const handlePrint = useReactToPrint({
        content: () => document.getElementById("printable-image"),
        documentTitle: `Redemption QR Codes`,
        removeAfterPrint: true,
    });

    // Draw QR codes on the canvas and convert it to a dataURL image
    useEffect(() => {
        if (canvasRef.current && badges?.length) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext("2d");

            if (ctx) {
                const width = size;
                const height = size;
                const canvasWidth = columns * (width + horizontalSpacing) + 2 * horizontalBorder;
                const canvasHeight = rows * (height + verticalSpacing) + 2 * verticalBorder;

                // Set canvas dimensions
                canvas.width = canvasWidth;
                canvas.height = canvasHeight;

                // Clear the canvas
                ctx.clearRect(0, 0, canvasWidth, canvasHeight);

                // Draw QR codes
                Array.from({ length: columns * rows }, (_, i) => i).forEach((place, i) => {
                    if (place + 1 >= startFrom && badges[place + 1 - startFrom]) {
                        const x = (i % columns) * (width + horizontalSpacing) + horizontalBorder * 2;
                        const y = Math.floor(i / columns) * (height + verticalSpacing) + verticalBorder * 2;

                        // Draw QR code on the canvas
                        QRCode.toCanvas(buildURL(badges[place + 1 - startFrom].id), { width: size, margin: 1 }, (error, qrCanvas) => {
                            if (!error && qrCanvas) {
                                ctx.drawImage(qrCanvas, x, y, size, size);
                            }
                        });
                    }
                });

                // Convert canvas to dataURL and store it in state
                const imageDataURL = canvas.toDataURL();
                setCanvasImage(imageDataURL);
            }
        }
    }, [badges, columns, rows, size, startFrom, verticalBorder, horizontalBorder, verticalSpacing, horizontalSpacing]);

    return (
        <>
            <Box direction="row" gap="small">
                <FormField label="Columns">
                    <TextInput type="number" min={1} max={20} value={columns} onChange={(e) => setColumns(e.target.valueAsNumber)} />
                </FormField>
                <FormField label="Rows">
                    <TextInput type="number" min={1} max={20} value={rows} onChange={(e) => setRows(e.target.valueAsNumber)} />
                </FormField>
                <FormField label="Start From">
                    <TextInput type="number" min={1} max={columns * rows} value={startFrom} onChange={(e) => setStartFrom(e.target.valueAsNumber)} />
                </FormField>
                <FormField label="Size">
                    <TextInput type="number" value={size} onChange={(e) => setSize(e.target.valueAsNumber)} />
                </FormField>
                <FormField label="Vertical Border">
                    <TextInput type="number" value={verticalBorder} onChange={(e) => setVerticalBorder(e.target.valueAsNumber)} />
                </FormField>
                <FormField label="Horizontal Border">
                    <TextInput type="number" value={horizontalBorder} onChange={(e) => setHorizontalBorder(e.target.valueAsNumber)} />
                </FormField>
                <FormField label="Vertical Spacing">
                    <TextInput type="number" value={verticalSpacing} onChange={(e) => setVerticalSpacing(e.target.valueAsNumber)} />
                </FormField>
                <FormField label="Horizontal Spacing">
                    <TextInput type="number" value={horizontalSpacing} onChange={(e) => setHorizontalSpacing(e.target.valueAsNumber)} />
                </FormField>
            </Box>
            <Box direction="row" gap="small">
                <Button label="Print" onClick={handlePrint}></Button>
            </Box>
            <Box>
                <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
                {canvasImage && <img id="printable-image" src={canvasImage} alt="QR codes" style={{ display: "block", margin: "0 auto" }} />}
            </Box>
        </>
    );
};

export default BadgePrint;
